import api from '@/libs/api'
import { v4 as uuidv4 } from 'uuid'
import store from '@/store'
import axios from 'axios'

const projectId = () => store.state.app.projectId

const { CancelToken } = axios
let cancelRequest = null

export default {
  namespaced: true,
  state: {
    serviceUnavailable: false,
    serviceRequested: false,
    showIntro: false,
    buckets: [],
    bucket: null,
    bucketEnums: null,
    bucketsResultInfo: null,
    fetchingBuckets: false,
    fetchingBucketEnums: false,
    gettingBucket: false,
    addingBucket: false,
    deletingBucket: false,
    updatingBucket: false,
    emptyingBucket: false,
    tags: [],
    bucketTags: [],
    tagKeys: [],
    fetchingTags: false,
    initializingStorage: false,
    validatingSubdomain: false,
  },
  mutations: {
    // API
    SET_BUCKETS(state, data) {
      state.buckets = data.result
      state.bucketsResultInfo = data.resultInfo
    },
    UPDATE_BUCKETS(state, data) {
      state.buckets = data
    },
    GET_BUCKET(state, bucket) {
      state.bucket = bucket
    },
    SET_FETCHING_BUCKETS(state, value) {
      state.fetchingBuckets = value
    },
    SET_FETCHING_BUCKET_ENUMS(state, value) {
      state.fetchingBucketEnums = value
    },
    SET_BUCKET_ENUMS(state, value) {
      const { jobStatusOptions, bucketJobOptions } = value
      state.bucketEnums = {
        jobStatusOptions,
        bucketJobOptions,
      }
    },
    SET_GETTING_BUCKET(state, value) {
      state.gettingBucket = value
    },
    SET_ADDING_BUCKET(state, value) {
      state.addingBucket = value
    },
    SET_DELETING_BUCKET(state, value) {
      state.deletingBucket = value
    },
    SET_UPDATING_BUCKET(state, value) {
      state.updatingBucket = value
    },
    SET_EMPTYING_BUCKET(state, value) {
      state.emptyingBucket = value
    },
    SET_TAGS(state, value) {
      const tags = value.map(tag => ({ tagKey: tag.key, tagValue: tag.value, id: uuidv4() }))
      const keys = []
      tags.forEach(element => { keys.push(element.id) })
      state.tags = tags
      state.tagKeys = keys
    },
    UPDATE_TAGS(state, value) {
      state.tags = value
    },
    SET_BUCKET_TAGS(state, value) {
      state.bucketTags = value
    },
    SET_FETCHING_TAGS(state, value) {
      state.fetchingTags = value
    },
    SET_UPDATING_TAGS(state, value) {
      state.fetchingTags = value
    },
    SET_INITIALIZING_STORAGE(state, value) {
      state.initializingStorage = value
    },
    SET_SERVICE_UNAVAILABLE(state, value) {
      state.serviceUnavailable = true
      state.serviceRequested = value
    },
    SET_SHOW_INTRO(state, value) {
      state.showIntro = value
    },
    SET_SUBDOAMIN_VALIDATE(state, value) {
      state.validatingSubdomain = value
    },
    CANCEL_FETCHING_BUCKETS() {
      if (cancelRequest) {
        cancelRequest()
      }
    },
  },
  actions: {
    async fetchBuckets({ commit }, accountId) {
      commit('SET_FETCHING_BUCKETS', true)
      commit('CANCEL_FETCHING_BUCKETS')
      const source = CancelToken.source()
      cancelRequest = source.cancel

      return new Promise((resolve, reject) => {
        api('storage').get(`/v2/projects/${projectId()}/account/${accountId}/buckets`, { cancelToken: source?.token })
          .then(response => {
            commit('SET_BUCKETS', response.data)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => {
            cancelRequest = null
            commit('SET_FETCHING_BUCKETS', false)
          })
      })
    },
    fetchBucketEnums({ commit }, accountId) {
      commit('SET_FETCHING_BUCKET_ENUMS', true)
      return new Promise((resolve, reject) => {
        api('storage').get(`/v2/projects/${projectId()}/account/${accountId}/buckets/model`)
          .then(response => {
            commit('SET_BUCKET_ENUMS', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_BUCKET_ENUMS', false) })
      })
    },

    addBucket({ commit }, { accountId, bucket }) {
      commit('SET_ADDING_BUCKET', true)

      return new Promise((resolve, reject) => {
        api('storage').post(`/v2/projects/${projectId()}/account/${accountId}/buckets`, bucket)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_ADDING_BUCKET', false) })
      })
    },

    updateBucket({ commit }, { accountId, query }) {
      commit('SET_UPDATING_BUCKET', true)

      return new Promise((resolve, reject) => {
        api('storage').put(`/v2/projects/${projectId()}/account/${accountId}/buckets/${query.bucketName}`, query)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_UPDATING_BUCKET', false) })
      })
    },

    getBucket({ commit }, { accountId, name }) {
      commit('SET_GETTING_BUCKET', true)

      return new Promise((resolve, reject) => {
        api('storage').get(`/v2/projects/${projectId()}/account/${accountId}/buckets/${name}`)
          .then(response => {
            commit('GET_BUCKET', response.data.result)

            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_GETTING_BUCKET', false) })
      })
    },

    // eslint-disable-next-line no-unused-vars
    validateSubdomain({ commit }, { accountId, name }) {
      commit('SET_SUBDOAMIN_VALIDATE', true)
      return new Promise(resolve => {
        api('storage').head(`/v2/projects/${projectId()}/account/${accountId}/buckets/${name.subdomain}`)
          .then(() => {
            resolve(false)
          })
          .catch(error => {
            if (error.response.status === 404) {
              resolve(true)
            }
            resolve(false)
          })
          .finally(() => { commit('SET_SUBDOAMIN_VALIDATE', false) })
      })
    },

    deleteBucket({ commit }, { accountId, bucket }) {
      commit('SET_DELETING_BUCKET', true)

      return new Promise((resolve, reject) => {
        api('storage').delete(`/v2/projects/${projectId()}/account/${accountId}/buckets/${bucket.name}?eventType=1`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_DELETING_BUCKET', false) })
      })
    },

    clearBucket({ commit }) {
      commit('SET_BUCKETS', { result: [], resultInfo: null })
    },

    emptyBucket({ commit }, { accountId, bucket }) {
      commit('SET_EMPTYING_BUCKET', true)

      return new Promise((resolve, reject) => {
        api('storage').delete(`/v2/projects/${projectId()}/account/${accountId}/buckets/${bucket.name}/?eventType=0`)
          .then(response => {
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_EMPTYING_BUCKET', false) })
      })
    },

    getBucketTags({ commit }, { accountId, name }) {
      commit('SET_FETCHING_TAGS', true)

      return new Promise((resolve, reject) => {
        api('storage').get(`/v2/projects/${projectId()}/account/${accountId}/buckets/${name.bucketId}/tags`)
          .then(response => {
            commit('SET_BUCKET_TAGS', response.data.result)
            commit('SET_TAGS', response.data.result)
            resolve(response)
          })
          .catch(error => {
            if (error.response.status === 404) {
              commit('SET_TAGS', [])
            }
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_TAGS', false) })
      })
    },
    updateBucketTags({ commit }, query) {
      commit('SET_FETCHING_TAGS', true)
      return new Promise((resolve, reject) => {
        api('storage').put(`/v2/projects/${projectId()}/account/${query.accountId}/buckets/${query.name}/tags`, { tags: query.tags })
          .then(response => {
            commit('SET_BUCKET_TAGS', response.data.result)
            commit('SET_TAGS', response.data.result)
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_TAGS', false) })
      })
    },
    deleteBucketTag({ commit }, query) {
      commit('SET_FETCHING_TAGS', true)
      const deleteAll = query.deleteAll ? '?deleteAll=true' : ''

      return new Promise((resolve, reject) => {
        api('storage').delete(`/v2/projects/${projectId()}/account/${query.accountId}/buckets/${query.name}/tags${deleteAll}`, { tags: query.tags })
          .then(response => {
            commit('SET_BUCKET_TAGS', [])
            commit('SET_TAGS', [])
            resolve(response)
          })
          .catch(error => {
            reject(error)
          })
          .finally(() => { commit('SET_FETCHING_TAGS', false) })
      })
    },
    startUsingStorage({ commit }) {
      commit('SET_INITIALIZING_STORAGE', true)
      return new Promise((resolve, reject) => {
        // TODOS: Change later to v2 when controller is added
        api('storage').get(`/v1/projects/${projectId()}/storage`)
          .then(res => {
            resolve(res)
          })
          .catch(err => {
            reject(err)
          })
          .finally(() => { commit('SET_INITIALIZING_STORAGE', false) })
      })
    },
  },
}
